import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../../api/config";
import OrdnerBatchDialog from "./OrdnerBatchDialog";
import GeminiWarnung from "../../components/GeminiWarnung";
import "./DokumentUmbenennen.css";

/**
 * Dokument umbenennen (Welle 100).
 * Einzeldatei: dieselbe Pipeline wie der Rename-me-Batch (v1 → Gemini → Gewinner → Namensregel),
 * nur für eine Datei schön dargestellt. Dazu der Ordner-Batch mit Ordnerwahl.
 */
const API = `${API_BASE}/rename-me-ocr/api`;

// Codes aus batch._filename_hard_error
const NOK_GRUND = {
  starts_with_hash: "Name beginnt mit # — Präfix nicht erkannt",
  contains_triple_hash: "Platzhalter ### im Namen — ein Teil wurde nicht erkannt",
  contains_triple_qmark: "Platzhalter ??? im Namen — ein Teil wurde nicht erkannt",
  contains_xxx: "Platzhalter XXX im Namen — Präfix nicht erkannt",
  hash_segment: "Ein Namensteil beginnt mit # — unsicher erkannt",
  invalid: "Ungültiger Dateiname",
};
// Anzeige absichern: Objekte ({wert, …}) und Listen werden zu Text
const alsText = (v) => {
  if (v && typeof v === "object") return Array.isArray(v) ? v.map(alsText).join(", ") : alsText(v.wert ?? v.value ?? "");
  return v == null ? "" : String(v);
};
const grundText = (g) => NOK_GRUND[g] || (g ? `Bitte prüfen (${g})` : "");

async function postJson(url, opts) {
  const r = await fetch(url, opts);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

function Werkzeug({ titel, hinweis, url, fertigText }) {
  const [laeuft, setLaeuft] = useState(false);
  const [text, setText] = useState("");
  const los = async () => {
    setLaeuft(true); setText("");
    try {
      const d = await postJson(url, { method: "POST", headers: { "Content-Type": "application/json" } });
      setText(d.success === false ? "Fehlgeschlagen" : fertigText(d));
    } catch (e) { setText(`Fehler: ${e.message}`); }
    finally { setLaeuft(false); }
  };
  return (
    <span className="du-werkzeug">
      <button type="button" className="du-btn text" onClick={los} disabled={laeuft} title={hinweis}>
        {laeuft ? `${titel} …` : titel}
      </button>
      {text && <small>{text}</small>}
    </span>
  );
}

export default function DokumentUmbenennenPage() {
  const [datei, setDatei] = useState(null);
  const [vorschauUrl, setVorschauUrl] = useState("");
  const [ziehen, setZiehen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState(null);
  const [ablegen, setAblegen] = useState({ laeuft: false, pfad: "" });
  const [ordnerDialog, setOrdnerDialog] = useState(false);
  const [zeigeTrace, setZeigeTrace] = useState(false);
  const eingabe = useRef(null);

  useEffect(() => () => { if (vorschauUrl) URL.revokeObjectURL(vorschauUrl); }, [vorschauUrl]);

  const auswerten = async (f) => {
    setLaeuft(true); setFehler(""); setErgebnis(null); setMeldung(null); setAblegen({ laeuft: false, pfad: "" });
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const d = await postJson(`${API}/einzeln`, { method: "POST", body: fd });
      setErgebnis(d); setName(d.name || "");
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setLaeuft(false);
    }
  };

  const waehle = (f) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") { setFehler("Bitte eine PDF-Datei wählen."); return; }
    setDatei(f);
    setVorschauUrl(URL.createObjectURL(f));
    auswerten(f);
  };

  const pdfName = () => {
    const n = (name || "").trim() || datei?.name || "Dokument.pdf";
    return /\.pdf$/i.test(n) ? n : `${n}.pdf`;
  };

  const herunterladen = () => {
    if (!datei) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(datei);
    a.download = pdfName();
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const inBatchOutput = async () => {
    if (!datei || !ergebnis) return;
    setAblegen({ laeuft: true, pfad: "" }); setMeldung(null);
    try {
      const fd = new FormData();
      fd.append("file", datei, datei.name);
      fd.append("name", pdfName());
      fd.append("original", datei.name);
      fd.append("info", JSON.stringify(ergebnis));
      const d = await postJson(`${API}/einzeln/ablegen`, { method: "POST", body: fd });
      setAblegen({ laeuft: false, pfad: d.pfad });
      setMeldung({ ok: true, text: `Im Batch Output abgelegt: ${d.name} — weiter wie gewohnt über „Verschieben".` });
    } catch (e) {
      setAblegen({ laeuft: false, pfad: "" });
      setMeldung({ ok: false, text: String(e.message || e) });
    }
  };

  const kopieren = async () => {
    try { await navigator.clipboard.writeText(pdfName()); setMeldung({ ok: true, text: "Dateiname kopiert." }); }
    catch { setMeldung({ ok: false, text: "Kopieren nicht möglich." }); }
  };

  const zuruecksetzen = () => {
    setDatei(null); setVorschauUrl(""); setErgebnis(null); setName(""); setFehler(""); setMeldung(null);
    setAblegen({ laeuft: false, pfad: "" });
    if (eingabe.current) eingabe.current.value = "";
  };

  const r = ergebnis;
  const geaendert = r && name !== r.name;
  const felder = r ? [
    ["Dokumenttyp", r.schema], ["Präfix", r.prefix], ["Absender", r.absender],
    ["Datum", r.datum], ["Rechnungsnr.", r.rechnungsnummer],
  ].map(([k, v]) => [k, alsText(v)]).filter(([, v]) => v) : [];

  const Vorschlag = ({ quelle, wert }) => (
    <button type="button" className={`du-vorschlag${wert && name === wert ? " gewaehlt" : ""}${r.gewinner === quelle ? " gewinner" : ""}`}
      onClick={() => wert && setName(wert)} disabled={!wert}
      title={wert ? "Diesen Namen übernehmen" : ""}>
      <span className="du-vorschlag-kopf">
        <b>{quelle === "gemini" ? "Gemini" : "v1 (Regeln)"}</b>
        {r.gewinner === quelle && <span className="du-abz gewinner">Gewinner</span>}
      </span>
      <span className="du-vorschlag-name">
        {wert || (quelle === "gemini"
          ? (r.gemini_gesperrt ? `nicht gelaufen: ${r.gemini_gesperrt}` : "nicht gelaufen (für diesen Dokumenttyp nicht nötig)")
          : "—")}
      </span>
    </button>
  );

  return (
    <div className="du-seite">
      <header className="du-kopf">
        <div>
          <h1>Dokument umbenennen</h1>
          <p>Gleiche Erkennung wie der Rename-me-Batch — v1 und Gemini, der bessere Name gewinnt.</p>
        </div>
        <button type="button" className="du-btn neben" onClick={() => setOrdnerDialog(true)}>
          📁 Ordner umbenennen …
        </button>
      </header>

      <GeminiWarnung gross wofuer="Dokumente werden gerade NUR mit v1 benannt — ohne Gemini. „Ordner umbenennen“ startet nicht, bis Gemini wieder geht." />

      {!datei && (
        <div className={`du-drop${ziehen ? " aktiv" : ""}`} role="button" tabIndex={0}
          onClick={() => eingabe.current?.click()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") eingabe.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
          onDragLeave={() => setZiehen(false)}
          onDrop={(e) => { e.preventDefault(); setZiehen(false); waehle(e.dataTransfer.files?.[0]); }}>
          <div className="du-drop-icon">📄</div>
          <div className="du-drop-titel">PDF hier ablegen oder klicken</div>
          <div className="du-drop-hinweis">Eine Datei — für mehrere Dateien „Ordner umbenennen" nutzen</div>
        </div>
      )}
      <input ref={eingabe} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
        onChange={(e) => waehle(e.target.files?.[0])} />

      {fehler && <div className="du-meldung fehler">{fehler}</div>}

      {datei && (
        <div className="du-raster">
          <section className="du-karte du-pdf">
            <div className="du-karte-kopf">
              <span className="du-dateiname" title={datei.name}>{datei.name}</span>
              <button type="button" className="du-btn text" onClick={zuruecksetzen}>Andere Datei</button>
            </div>
            {vorschauUrl && <iframe title="PDF-Vorschau" src={`${vorschauUrl}#view=FitH&navpanes=0`} />}
          </section>

          <section className="du-karte du-ergebnis">
            {laeuft && (
              <div className="du-laden">
                <div className="du-spinner" />
                <div><b>Wird ausgewertet …</b><br />v1 und Gemini lesen das Dokument — das dauert etwa 20–60 Sekunden.</div>
              </div>
            )}

            {!laeuft && r && (
              <>
                <label className="du-label" htmlFor="du-name">Neuer Dateiname</label>
                <div className="du-name-zeile">
                  <input id="du-name" className={`du-name${r.nok ? " nok" : ""}`} value={name}
                    onChange={(e) => setName(e.target.value)} spellCheck={false} />
                  <button type="button" className="du-btn text" onClick={kopieren} title="Dateiname kopieren">Kopieren</button>
                </div>
                <div className="du-name-info">
                  {geaendert
                    ? <>Von Hand geändert · <button type="button" className="du-link" onClick={() => setName(r.name)}>zurück zum Vorschlag</button></>
                    : <>Vorschlag von <b>{r.gewinner === "gemini" ? "Gemini" : "v1"}</b></>}
                </div>
                {r.nok && <div className="du-meldung nok">⚠ {grundText(r.nok)} — Name vor dem Speichern prüfen.</div>}

                <div className="du-vorschlaege">
                  <Vorschlag quelle="v1" wert={r.v1} />
                  <Vorschlag quelle="gemini" wert={r.gemini} />
                </div>

                {felder.length > 0 && (
                  <dl className="du-felder">
                    {felder.map(([k, v]) => (<React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>))}
                  </dl>
                )}
                {r.duplikat && <div className="du-hinweis">Ausgangsrechnung: beim Ablegen entsteht zusätzlich „{r.duplikat}".</div>}

                <div className="du-aktionen">
                  <button type="button" className="du-btn haupt" onClick={herunterladen}>Herunterladen</button>
                  <button type="button" className="du-btn neben" onClick={inBatchOutput}
                    disabled={ablegen.laeuft || !!ablegen.pfad}>
                    {ablegen.laeuft ? "Lege ab …" : ablegen.pfad ? "✓ Im Batch Output" : "In Batch Output legen"}
                  </button>
                  <button type="button" className="du-btn text" onClick={() => auswerten(datei)}>Neu auswerten</button>
                </div>
                {meldung && <div className={`du-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}

                {r.trace?.length > 0 && (
                  <div className="du-trace">
                    <button type="button" className="du-link" onClick={() => setZeigeTrace((v) => !v)}>
                      {zeigeTrace ? "Ablauf ausblenden" : "Ablauf anzeigen"}
                    </button>
                    {zeigeTrace && <pre>{r.trace.join("\n")}</pre>}
                  </div>
                )}
              </>
            )}

            {!laeuft && !r && !fehler && <div className="du-leer">Noch kein Ergebnis.</div>}
          </section>
        </div>
      )}

      <footer className="du-werkzeuge">
        <span className="du-werkzeuge-titel">Werkzeuge für den Batch Output</span>
        <Werkzeug titel="Duplikate" url={`${API}/duplicates/create`}
          hinweis="Erstellt Duplikate für HIG-Eingangsrechnungen in der Dropbox-Outbox"
          fertigText={(d) => `${d.created ?? 0} erstellt, ${d.skipped ?? 0} übersprungen`} />
        <Werkzeug titel="BWA → UStVA" url={`${API}/bwa/extract-ustva`}
          hinweis="Extrahiert UStVA-Seiten aus BWA-Gesamt-Dateien in der Dropbox-Outbox"
          fertigText={(d) => `${d.created ?? 0} erstellt, ${d.skipped ?? 0} übersprungen`} />
        <Werkzeug titel="N26 aufteilen" url={`${API}/n26/split`}
          hinweis="Teilt N26-Kontoauszüge nach IBAN (Hauptkonto + Spaces) in der Dropbox-Inbox"
          fertigText={(d) => `${d.processed ?? 0} verarbeitet, ${d.created ?? 0} erstellt`} />
        <Link className="du-alt" to="/rename-alt" title="Alte Ansicht mit OCR-Overlay und Feld-Korrekturen">Entwickler-Ansicht</Link>
      </footer>

      {ordnerDialog && <OrdnerBatchDialog onSchliessen={() => setOrdnerDialog(false)} />}
    </div>
  );
}
